import React from 'react';
import moment from 'moment';
import type { DateRangePreset, Granularity } from '../../types/timeframe';
import { DATE_RANGE_PRESETS, DAILY_DATE_RANGE_PRESETS, QUARTERLY_DATE_RANGE_PRESETS } from '../../types/timeframe';
import { computeDateRange, getQuarterFromDate } from '../../pages/usageReportingContainer/utils/computeDateRange';
import styles from './dateRangeSelector.module.css';

interface DateRangeSelectorProps {
  value: DateRangePreset;
  onChange: (preset: DateRangePreset, startDate: string, endDate: string) => void;
  granularity?: Granularity;
}

const PRESET_LABELS: Record<DateRangePreset, string> = {
  thisMonth:     'This Month',
  lastMonth:     'Last Month',
  thisYear:      'This Year',
  past12Months:  'Past 12 Months',
  thisQuarter:   'This Quarter',
  lastQuarter:   'Last Quarter',
  last4Quarters: 'Last 4 Quarters',
  custom:        'Custom',
};

function formatSubtitle(preset: DateRangePreset): string {
  if (preset === 'custom') return '';
  const { startDate, endDate } = computeDateRange(preset);
  
  // For quarterly presets, show quarter format
  if (preset === 'thisQuarter' || preset === 'lastQuarter') {
    const { quarter, year } = getQuarterFromDate(startDate);
    return `Q${quarter} ${year}`;
  }
  if (preset === 'last4Quarters') {
    const startQ = getQuarterFromDate(startDate);
    const endQ = getQuarterFromDate(endDate);
    return `Q${startQ.quarter} ${startQ.year} - Q${endQ.quarter} ${endQ.year}`;
  }
  
  if (preset === 'thisYear') {
    return moment(startDate).format('YYYY');
  }
  const start = moment(startDate);
  const end = moment(endDate);
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange, granularity }) => {
  const handleSelect = (preset: DateRangePreset) => {
    const { startDate, endDate } = computeDateRange(preset, granularity);
    onChange(preset, startDate, endDate);
  };

  // Use preset list appropriate to the selected granularity
  const presets =
    granularity === 'quarterly' ? QUARTERLY_DATE_RANGE_PRESETS :
    granularity === 'daily'     ? DAILY_DATE_RANGE_PRESETS :
    DATE_RANGE_PRESETS;

  return (
    <div className={styles.container}>
      <p className={styles.heading}>DATE RANGE</p>
      <ul className={styles.list}>
        {presets.map(preset => (
          <li
            key={preset}
            className={`${styles.item} ${value === preset ? styles.selected : ''}`}
            onClick={() => handleSelect(preset)}
            role="button"
            aria-pressed={value === preset}
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && handleSelect(preset)}
          >
            <span className={styles.label}>{PRESET_LABELS[preset]}</span>
            {preset !== 'custom' && (
              <span className={styles.subtitle}>{formatSubtitle(preset)}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DateRangeSelector;
